import { useCallback, useEffect, useState } from "react";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { STEP_LABELS } from "@/lib/api";
import { prod, errMsg, fmtDate } from "@/lib/prodApi";

const STATUS = {
  locked: "Locked until all four clues are recovered",
  open: "Open — all four clues recovered",
  unlocked_early: "Unlocked early by the host",
  decided: "Charge decision made",
};

function Stat({ label, value, testid }) {
  return <div className="panel p-6"><div className="text-[17px] text-[#505a5f]">{label}</div><div className="text-3xl font-black mt-1" data-testid={testid}>{value}</div></div>;
}

function ResetDialog({ open, setOpen, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await prod.post("/case/reset", { confirm: text });
      onDone(data);
      toast.success("Case reset for the next show");
      setOpen(false);
    } catch (e) {
      toast.error(errMsg(e, "Reset failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); setText(""); }}>
      <AlertDialogContent className="rounded-none border-2 border-[#0b0c0c] max-w-xl" data-testid="reset-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-3xl font-black">Reset for next show?</AlertDialogTitle>
          <AlertDialogDescription className="text-lg text-[#0b0c0c]">This clears all progress, chats, case notes and the verdict. Videos and audio are kept.</AlertDialogDescription>
        </AlertDialogHeader>
        <label htmlFor="reset-confirm-input" className="form-label">Type RESET to confirm</label>
        <input id="reset-confirm-input" value={text} onChange={(e) => setText(e.target.value)} className="form-input w-60" autoComplete="off" data-testid="reset-confirm-input" />
        <AlertDialogFooter className="gap-3">
          <AlertDialogCancel className="btn-ghost h-auto" data-testid="reset-cancel">Cancel</AlertDialogCancel>
          <button className="btn-spore" disabled={text !== "RESET" || busy} onClick={go} data-testid="reset-confirm-button">Reset the case</button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function CaseProgress() {
  const [c, setC] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const reload = useCallback(() => prod.get("/case").then((r) => setC(r.data)).catch(() => {}), []);
  useEffect(() => {
    reload();
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
  }, [reload]);
  if (!c) return <p className="text-lg py-8">Loading case progress…</p>;

  const toggle = async (enabled) => {
    try {
      const { data } = await prod.post("/case/charge-early", { enabled });
      setC(data);
      toast.success(enabled ? "Charge decision unlocked early" : "Early unlock turned off");
    } catch (e) {
      toast.error(errMsg(e, "Could not change the setting"));
    }
  };
  const v = c.verdict;
  return (
    <div data-testid="case-progress-section">
      <div className="flex items-end gap-4 mb-8">
        <div className="flex-1"><h2 className="text-4xl font-black">Case progress</h2><p className="text-lg text-[#505a5f] mt-2">Live view of the shared case. Refreshes every 5 seconds.</p></div>
        <button className="btn-spore btn-danger" onClick={() => setResetOpen(true)} data-testid="reset-show-button"><RotateCcw size={18} /> Reset for next show</button>
      </div>
      <div className="grid grid-cols-4 gap-6">
        <Stat label="Clues unlocked" value={`${c.clues_unlocked}/4`} testid="progress-clues" />
        <Stat label="Secrets revealed" value={`${c.secrets_revealed}/4`} testid="progress-secrets" />
        <Stat label="Case notes" value={c.notes_count} testid="progress-notes" />
        <Stat label="Charge decision" value={<span className="text-xl">{STATUS[c.charge_status]}</span>} testid="progress-charge-status" />
      </div>
      <div className="panel p-6 mt-6 flex items-center gap-5" data-testid="charge-early-panel">
        <Switch id="charge-early" checked={c.charge_early} onCheckedChange={toggle} disabled={!!v} data-testid="charge-early-toggle" />
        <label htmlFor="charge-early" className="text-xl font-bold">Unlock charge decision early</label>
        <span className="text-[17px] text-[#505a5f]">Lets players charge a suspect with fewer than 4 clues.</span>
      </div>
      <div className="panel mt-6" data-testid="progress-verdict">
        <div className="px-6 py-4 border-b border-[#b1b4b6] text-2xl font-black">Verdict</div>
        {v ? (
          <dl className="p-6 grid grid-cols-[200px_1fr] gap-y-3 text-lg">
            <dt className="font-bold">Charged</dt><dd data-testid="progress-verdict-suspect">{v.suspect_name}</dd>
            <dt className="font-bold">Result</dt><dd><strong className={`tag ${v.correct ? "tag-green" : "bg-[#f6d7d2] text-[#942514]"}`} data-testid="progress-verdict-result">{v.correct ? "Correct — CASE CLOSED" : "Wrong — THE SPORES REVEAL"}</strong></dd>
            <dt className="font-bold">Reason</dt><dd className="italic" data-testid="progress-verdict-reason">"{v.reason}"</dd>
            <dt className="font-bold">Filed</dt><dd>{fmtDate(v.at)}</dd>
          </dl>
        ) : <p className="p-6 text-lg text-[#505a5f]" data-testid="progress-no-verdict">No verdict yet.</p>}
      </div>
      <table className="w-full mt-6 panel text-[17px]" data-testid="progress-suspects-table">
        <thead><tr className="bg-[#f3f2f1] text-left">
          <th className="p-4">Suspect</th>{STEP_LABELS.map(([k, l]) => <th key={k} className="p-4">{l}</th>)}<th className="p-4">Chat messages</th>
        </tr></thead>
        <tbody>
          {c.suspects.map((s) => (
            <tr key={s.id} className="border-t border-[#b1b4b6]" data-testid={`progress-row-${s.id}`}>
              <td className="p-4 font-bold">{s.name}</td>
              {STEP_LABELS.map(([k]) => <td key={k} className="p-4"><span className={`tick ${s.progress[k] ? "on" : ""}`} data-testid={`progress-${s.id}-${k}`}>✓</span></td>)}
              <td className="p-4" data-testid={`progress-${s.id}-chats`}>{s.mode === "ai" ? s.chat_messages : "Live actor"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <ResetDialog open={resetOpen} setOpen={setResetOpen} onDone={setC} />
    </div>
  );
}
